import * as d3 from 'd3';
// import '../styles/eventTimeline.css'

export default class eventTimeline {
  constructor(config) {
    this.root = d3.select(config.elem);

    this.viewBox = {
      xPadding: 30,
      yPadding: 50,
      width: 12 ** 3,
      height: 12 ** 2,
    };

    //NOTE THAT THIS MUST BE IN ISO FORMAT, IE MATCH RESULT OF date.toISOString()
    this.xAccessor = config.xAccessor || 'phone_time';
    this.keyAccessor = config.keyAccessor || 'id';
    this.timeIncrement = config.timeIncrement || 15;

    this.eventRectPadding = 3;
    this.TRANSITION_DURATION = 500;

    this.onEventBoxMouseEnter = config.onEventBoxMouseEnter || (() => null);
    this.onEventBoxMouseLeave = config.onEventBoxMouseLeave || (() => null);
  }

  initiate = (data) => {
    this.wrapper = this.root
      .append('svg')

      .attr(
        'viewBox',
        '0 0 ' +
          (this.viewBox.width + this.viewBox.xPadding * 2) +
          ' ' +
          (this.viewBox.height + this.viewBox.yPadding * 2)
      );

    this.bounds = this.wrapper
      .append('g')
      .attr('transform', `translate(${this.viewBox.xPadding}, ${this.viewBox.yPadding})`);

    this.eventsBars = this.bounds.append('g').attr('class', 'event-bars');

    this.axisBottom = this.bounds.append('g');

    this.update(data);
  };

  update = (data) => {
    this.buildTimescale(data);
    this.buildEventBars(data);
  };

  buildTimescale = (data) => {
    let sortedData = data.sort((a, b) => (a[this.xAccessor] > b[this.xAccessor] ? 1 : -1));
    let domain = [
      new Date(sortedData[0][this.xAccessor]),
      new Date(sortedData[sortedData.length - 1][this.xAccessor]),
    ];
    let range = [0, this.viewBox.width];
    this.timescale = d3.scaleTime(domain, range);

    //remove, in case this an update operation
    d3.selectAll('.axis-bottom').remove();

    this.axisBottomMajor = this.axisBottom
      .append('g')
      .attr('class', 'axis-bottom')
      .attr('transform', `translate(0, ${this.viewBox.height})`);
    this.axisBottomMinor = this.axisBottom
      .append('g')
      .attr('class', 'axis-bottom')
      .attr('transform', `translate(0, ${this.viewBox.height})`);

    //MAJOR AXIS WITH LABEL
    this.axisBottomMajor
      .transition()
      .duration(this.TRANSITION_DURATION)
      .call(
        d3
          .axisBottom()
          .scale(this.timescale)
          .ticks(this.timeIncrement <= 30 ? d3.timeMinute.every(60) : d3.timeHour.every(4))
          .tickFormat((d) =>
            this.timeIncrement <= 30 ? d3.timeFormat('%I %p')(d) : d3.timeFormat('%H')(d)
          )
          .tickSizeOuter(0)
          .tickSizeInner(10)
          .tickPadding(12)
      );

    //MINOR AXIS -- SMALLER TICKS AND NO LABEL
    this.axisBottomMinor
      .transition()
      .duration(this.TRANSITION_DURATION)
      .call(
        d3
          .axisBottom()
          .scale(this.timescale)
          .ticks(d3.timeMinute.every(this.timeIncrement))
          .tickFormat((d) => null)
          .tickSizeOuter(0)
          .tickSizeInner(5)
          .tickPadding(12)
      );

    this.axisBottomMajor.selectAll('path').attr('opacity', 0);
    this.axisBottomMinor.selectAll('path').attr('opacity', 0);

    this.axisBottom.selectAll('text').attr('font-size', '3em').attr('font-weight', 'bold');
  };

  //rounds times to configured timeIncrement
  normalizeTimesAndGroupData = (data) => {
    data = data
      //filter out the keys that aren't valid -- this will break the chart
      .filter((d) => !isNaN(new Date(d[this.xAccessor]).valueOf()))
      //this map returns the normalized time,
      .map((d) => {
        //remember result of accessor MUST BE ISO TIME STRING
        let normalized_date = new Date(d[this.xAccessor]);
        normalized_date.setMilliseconds(0);
        normalized_date.setSeconds(0);
        normalized_date.setMinutes(
          Math.floor(normalized_date.getMinutes() / this.timeIncrement) * this.timeIncrement
        );

        return {
          ...d,
          normalized_date,
        };
      });

    //now, count how many events are in each time increment window
    let window_map = {};
    data.forEach((d) => {
      let t = d.normalized_date.getTime();
      if (window_map[t]) {
        window_map[t].push(d);
      } else {
        window_map[t] = [d];
      }
      //add y position to data
      d.increment_y_position = window_map[t].length;
    });

    data = data.map((d) => {
      let t = d.normalized_date.getTime();
      return {
        ...d,
        increment_events_list: window_map[t], // || [d]
      };
    });

    return data;
  };

  //need to know how wide each of the bars is, based on spread of time in data and time increment
  //I have to have built the timescale and normalized time for this to work
  //i compare the x assignment of the first timestamp in the data and that time + the time increment to get widht
  getEventRectWidth = (data) => {
    const sample_date = new Date(data[0].normalized_date.getTime());
    const x1 = this.timescale(sample_date);
    sample_date.setMinutes(sample_date.getMinutes() + this.timeIncrement);
    const x2 = this.timescale(sample_date);

    return x2 - x1 - this.eventRectPadding;
  };

  getRectHeight = (data) => {
    return 10;
  };

  buildEventBars = (data) => {
    const _classThis = this; // for use in functions where we need the d3 context, but class functions

    //start by rounding data into 15 minute increments

    data = this.normalizeTimesAndGroupData(data);

    const rectWidth = this.getEventRectWidth(data);
    const rectHeight = this.getRectHeight(data);
    const rectPadding = 2;

    const enter = (selection) => selection.append('rect').attr('class', 'event-rect');

    const update = (selection) => selection;

    const exit = (selection) => selection.remove(); //todo: transition before remove?

    this.eventRects = this.eventsBars
      .selectAll('.event-rect')
      .data(data, (d) => d[this.keyAccessor])
      .join(enter, update, exit)
      .attr('width', rectWidth)
      .attr('height', this.getRectHeight(data))
      .attr('x', (d) => this.timescale(d.normalized_date))
      .attr('fill', (d) => d.color || 'red')
      .attr('y', (d) => this.viewBox.height);

    this.eventRects
      .transition()
      .duration(this.TRANSITION_DURATION)
      .attr('y', (d) => this.viewBox.height - d.increment_y_position * (rectHeight + rectPadding));

    d3.selectAll('.event-rect').on('mouseenter', function () {
      const _t = d3.select(this);
      let _d = _t.data();
      _d = _d && _d.length ? _d[0] : {};
      _t.attr('stroke', 'white').attr('stroke-width', 4);
      _classThis.onEventBoxMouseEnter(_d);
    });

    d3.selectAll('.event-rect').on('mouseleave', function () {
      const _t = d3.select(this);
      let _d = _t.data();
      _d = _d && _d.length ? _d[0] : {};
      _t.attr('stroke', 'none');
      _classThis.onEventBoxMouseLeave(_d);
    });
  };
}
